import ProductAsyncSwitcher from './ProductAsyncSwitcher';
import ProductImages from './ProductImages';
import ProductItem from './ProductItem';
import Quantity from '../Quantity';
import { closeModal } from '../../helpers/modals';
import initCarousel from '../../helpers/carousel';
import {
  cartApi,
  getTranslation,
  showQuickshop,
  unitlessBreakpoints,
  updateCart,
} from '../../helpers';

const selectors = {
  mobileBlocks: '[data-mobile-blocks]',
  form: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  floatingAtcBar: '[data-floating-atc-bar]',
  floatingAddToCart: '[data-floating-add-to-cart]',
  floatingAtcLabel: '[data-floating-atc-label]',
  productSelect: '[data-product-select]',
  optionInput: '[name^="options"]',
  modalContent: 'quickshop-content',
  modalProduct: '[data-product]',
  productImage: '[data-product-image]',
  mainCarousel: '[data-image-carousel]',
  thumbnailCarousel: '[data-thumbnail-carousel]',
  complementaryProducts: '[data-complementary-products]',
  scrollToReviews: '[data-scroll-to-reviews]',
  okendoReviews: '[data-oke-widget]',
};

/**
 * The wrapper class for PDP components
 *
 * @export
 * @class Product
 */
export default class Product {
  /**
   * Creates an instance of Product.
   *
   * @param {HTMLElement} el The container of this product
   * @param {Object} config Settings object
   * @param {String} config Section ID
   */
  constructor(el, config = {}) {
    this.el = el;
    this._pruneMobileBlocks();

    this.section = this._getSectionElement(this.el);
    this.sectionId = this.section.id.replace('shopify-section-', '');
    this.config = Object.assign(
      {
        isQuickShop: false,
        isFeaturedProduct: false,
        quickShopUrl: '',
        modal: null,
      },
      config,
    );

    this.formElement = this.el.querySelector(selectors.form);
    this.addToCartButton = this.formElement.querySelector(selectors.addToCart);
    this.floatingAtcBar = this.el.querySelector(selectors.floatingAtcBar);
    this.floatingAtcButton = this.floatingAtcBar?.querySelector(
      selectors.floatingAddToCart,
    );
    this.floatingAtcLabel = this.floatingAtcBar?.querySelector(
      selectors.floatingAtcLabel,
    );
    this.optionInputs = this.formElement.querySelectorAll(
      selectors.optionInput,
    );

    this.complementaryProductsContainers = this.el.querySelectorAll(
      selectors.complementaryProducts,
    );

    // Scroll to Okendo reviews widget — delegated + capture so inner Okendo links
    // (star rating) cannot swallow the click before it reaches [data-scroll-to-reviews].
    // Note: `{{ blocks }}` is rendered twice (mobile + desktop).
    this.el.addEventListener('click', this._onScrollToReviewsClick, true);

    this._initComponents();
    this._initFloatingAtc();
  }

  /**
   * Init all components that make up the product
   */
  _initComponents() {
    this.productImages = new ProductImages(this.el);
    this._initProductForm();

    if (this.complementaryProductsContainers.length) {
      this._initComplementaryProducts();
    }

    new ProductAsyncSwitcher(this.el, this.sectionId);
  }

  _initFloatingAtc() {
    if (
      this.config.isQuickShop ||
      !this.floatingAtcBar ||
      !this.addToCartButton
    ) {
      return;
    }

    this.floatingAtcButton?.addEventListener('click', () => {
      if (this.addToCartButton.disabled) return;
      this.addToCartButton.click();
    });

    this._floatingAtcObserver = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast =
          !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.floatingAtcBar.classList.toggle('is-visible', scrolledPast);
        this.floatingAtcBar.setAttribute(
          'aria-hidden',
          scrolledPast ? 'false' : 'true',
        );
      },
      { threshold: 0, rootMargin: '0px' },
    );

    this._floatingAtcObserver.observe(this.addToCartButton);
  }

  _initProductForm() {
    this.addToCartButton?.addEventListener(
      'click',
      this._onAddToCart.bind(this),
    );

    this.optionInputs?.forEach(optionInput => {
      optionInput.addEventListener('change', this._onOptionChange.bind(this));
    });

    new Quantity(this.formElement);
  }

  _onScrollToReviewsClick = e => {
    const trigger = e.target.closest(selectors.scrollToReviews);
    if (!trigger || !this.el.contains(trigger)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const reviewsWidget = document.querySelector(selectors.okendoReviews);
    if (reviewsWidget) {
      const offset = 100;
      const elementPosition = reviewsWidget.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  _initComplementaryProducts() {
    const carouselOptions = {
      perPage: 2,
      gap: '0.5rem',
      mediaQuery: 'min',
      pagination: false,
      breakpoints: {
        [unitlessBreakpoints.sm]: {
          perPage: 3,
        },
        [unitlessBreakpoints.md]: {
          perPage: 2,
        },
        [unitlessBreakpoints.lg]: {
          padding: {
            right: 80,
          },
        },
        [unitlessBreakpoints.xl]: {
          perPage: 3,
          padding: {
            right: 40,
          },
        },
      },
    };

    this.complementaryProductsContainers.forEach(container => {
      const splide = initCarousel(container, carouselOptions, true);
      if (splide) {
        const productItems = container.querySelectorAll('[data-product-item]');
        productItems.forEach(productItem => new ProductItem(productItem));
      }
    });
  }

  _getSectionElement = el => {
    return el.closest('.shopify-section');
  };

  /**
   * Remove direct child divs without mobile-above class from data-mobile-blocks
   */
  _pruneMobileBlocks() {
    const mobileBlocks = this.el.querySelector(selectors.mobileBlocks);
    if (!mobileBlocks) return;

    Array.from(mobileBlocks.children).forEach(child => {
      if (
        child instanceof HTMLDivElement &&
        !child.classList.contains('mobile-above')
      ) {
        child.remove();
      }
    });
  }

  _onAddToCart = e => {
    e.preventDefault();

    const formData = new FormData(this.formElement);
    const data = cartApi.formatFormData(formData);

    this._toggleLoading(true, getTranslation('products.adding'));

    cartApi
      .add({ items: [data] })
      .then(response => {
        this._toggleLoading(false, getTranslation('products.added'));
        this._reRenderSection(this.formElement.dataset.productUrl);

        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }

        if (this.config.isQuickShop) {
          closeModal('quickshop');
        }
      })
      .catch(error => {
        console.error(error);
        this._toggleLoading(false, getTranslation('products.error'));
      });
  };

  _getSelectedOptions = () => {
    return Array.from(this.optionInputs)
      .reduce((arr, input) => {
        if (
          input instanceof HTMLInputElement &&
          input.type === 'radio' &&
          input.checked
        ) {
          arr.push(input.dataset.valueId);
        }

        if (input instanceof HTMLSelectElement) {
          const selectedOption = input.options[input.selectedIndex];
          arr.push(selectedOption.dataset.valueId);
        }

        return arr;
      }, [])
      .join(',');
  };

  _onOptionChange = e => {
    let productUrl = this.formElement.dataset.productUrl;
    let variantId;
    let isCombinedListing = false;

    if (e.target instanceof HTMLInputElement && e.target.type === 'radio') {
      // Check if changed option has associated product URL [Combined Listings]
      const { variantId: optionVariantId, productUrl: optionProductUrl } =
        e.target.dataset;

      if (optionProductUrl) {
        productUrl = optionProductUrl;
        isCombinedListing = true;
      }

      if (optionVariantId) {
        variantId = optionVariantId;
      }
    }

    if (e.target instanceof HTMLSelectElement) {
      const { productUrl: optionProductUrl, variantId: optionVariantId } =
        e.target.options[e.target.selectedIndex].dataset;

      if (optionProductUrl) {
        productUrl = optionProductUrl;
        isCombinedListing = true;
      }

      if (optionVariantId) {
        variantId = optionVariantId;
      }
    }

    let newURL;

    if (isCombinedListing) {
      newURL = productUrl;
    } else {
      newURL = new URL(window.location.href);
      newURL.searchParams.set('variant', variantId);
      newURL = newURL.toString();
    }

    this._toggleLoading(true, getTranslation('products.loading'));
    this._reRenderSection(productUrl);

    if (!this.config.isQuickShop && !this.config.isFeaturedProduct) {
      this._appendHistory(newURL);
    }
  };

  _reRenderSection = url => {
    if (this.config.isQuickShop) {
      url = this.config.quickShopUrl;
    } else {
      url = `${url}?section_id=${this.sectionId}`;
    }

    const selectedOptions = this._getSelectedOptions();

    if (selectedOptions.length) {
      url += `&option_values=${selectedOptions}`;
    }

    fetch(url)
      .then(response => response.text())
      .then(data => {
        if (this.config.isQuickShop) {
          const quickshopHtml = showQuickshop(data);
          const quickShopContent = document.getElementById(
            selectors.modalContent,
          );
          quickShopContent.innerHTML = quickshopHtml;

          const product = quickShopContent.querySelector(
            selectors.modalProduct,
          );

          // Create a new Product instance for the requested quickshop product
          new Product(product, this.config);
        } else {
          this.section.outerHTML = data;
          this.section = document.getElementById(
            `shopify-section-${this.sectionId}`,
          );
          const product = this.section.querySelector('[data-product]');
          new Product(product, this.config);
        }
      });
  };

  _appendHistory(url) {
    window.history.pushState({ path: url }, '', url);
  }

  _toggleLoading(loading = false, buttonText) {
    if (loading) {
      this.formElement.classList.add('loading');
      this.addToCartButton.disabled = true;
      this.optionInputs?.forEach(optionInput => (optionInput.disabled = true));
    } else {
      this.formElement.classList.remove('loading');
      this.addToCartButton.removeAttribute('disabled');
      this.optionInputs?.forEach(optionInput =>
        optionInput.removeAttribute('disabled'),
      );
    }

    if (this.floatingAtcButton) {
      if (loading) {
        this.floatingAtcButton.disabled = true;
      } else {
        this.floatingAtcButton.disabled = this.addToCartButton.disabled;
      }
    }

    if (buttonText) {
      this.addToCartButton.innerHTML = buttonText;
      if (this.floatingAtcLabel) {
        this.floatingAtcLabel.textContent = buttonText;
      }
    }
  }
}
